import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import Autocomplete from '@material-ui/lab/Autocomplete'

import Button from '@eig-builder/core-ui/Button'
import Box from '@eig-builder/core-ui/Box'
import CircularProgress from '@eig-builder/core-ui/CircularProgress'
import TextField from '@eig-builder/core-ui/TextField'
import Typography from '@eig-builder/core-ui/Typography'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'
import useDataApi from '@eig-builder/core-utils/hooks/useDataApi'
import usePostAsJson from '@eig-builder/core-utils/hooks/usePostAsJson'

import { showSuccess } from '@eig-builder/module-notification-bar-with-context/actions'
import { NotificationBarContext } from '@eig-builder/module-notification-bar-with-context'

import countries from 'core/countries'

import ButtonWithLoader from 'core/components/button-with-loader/index'
import Modal from 'core/components/modal/index'

import { getCrmApiOptions } from 'modules/crm/helpers/fetch-options-helper'

const ChangeBillingCountry = ({ account, handleClose }) => {
  const [currentReq] = useDataApi(getCrmApiOptions(account, '/v1.0/billing/billing_country', 'text'))
  const [changeBillingCountryReq, changeBillingCountry] = usePostAsJson()

  const [newBillingCountry, setNewBillingCountry] = useState(null)
  const [error, setError] = useState('')

  const { dispatch: notificationDispatch } = useContext(NotificationBarContext)

  useEffect(() => {
    if (currentReq.data) {
      const option = countries.find(c => c.value === currentReq.data)
      option && setNewBillingCountry(option)
    }
  }, [currentReq.data])

  useEffect(() => {
    if (changeBillingCountryReq.isSuccess) {
      handleClose({ refetchAccountDetails: true })
      showSuccess(`The billing country has been updated to: ${newBillingCountry.label} (${newBillingCountry.value}) `)(notificationDispatch)
    }
  }, [changeBillingCountryReq.isSuccess])

  const handleCountryChange = (e, option) => {
    if (error) {
      setError('')
    }
    setNewBillingCountry(option)
  }

  const handleSubmit = () => {
    if (newBillingCountry && newBillingCountry.value) {
      changeBillingCountry({}, getCrmApiOptions(account, `/v1.0/billing/change_billing_country?countryCode=${newBillingCountry.value}`))
    } else {
      setError('Please select a country')
    }
  }

  const actions = [
    <Button
      key='change-billing-country-cancel'
      color='secondary'
      disabled={changeBillingCountryReq.isLoading}
      onClick={handleClose}
      dataElementLocation={DataElementLocations.POP_UP}
      dataElementLabel='cst-account-details--change-billing-country--cancel'
      dataElementId='cst-account-details--change-billing-country--cancel'
    >
      Cancel
    </Button>,
    <ButtonWithLoader
      key='change-billing-country-save'
      onClick={handleSubmit}
      dataElementSuffix='account-details--change-billing-country--save'
      showLoader={changeBillingCountryReq.isLoading}
      text='Save'
    />
  ]

  return <Modal
    actions={actions}
    dataElementPrefix='cst-account-details--change-billing-country'
    disableBackdropClick
    handleClose={handleClose}
    maxWidth='xs'
    open
    title='Change Billing Country'
  >
    {currentReq.isLoading
      ? <CircularProgress centered />
      : <>
        <Autocomplete
          size='small'
          getOptionLabel={o => `${o.label} (${o.value})`}
          getOptionSelected={o => o.value === newBillingCountry.value}
          onChange={handleCountryChange}
          options={countries}
          value={newBillingCountry}
          renderInput={(params) =>
            <TextField
              {...params}
              variant='outlined'
              label='Billing Country'
              error={!!error}
              helperText={error}
              dataElementLocation={DataElementLocations.POP_UP}
              dataElementLabel='cst-account-details--change-billing-country--field'
              dataElementId='cst-account-details--change-billing-country--field'
            />}
        />
        <Box mt={4}>
          <Typography variant='body1'>
            Please note: This will update the taxes, currency and Ubersmith brand of a customer. Please verify the results afterwards.
          </Typography>
        </Box>
      </>
    }

  </Modal>
}

ChangeBillingCountry.propTypes = {
  account: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired
}

export default ChangeBillingCountry
